import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/img/logo.png"; // Ensure this path is correct
import { useQuery } from "@tanstack/react-query";
import { AuthContext } from "../context/AuthProvider"; 
import { KeyContext } from "../context/SearchKeyProvider"; 
import { CartContext } from "../context/CartProvider"; 
import { WishListContext } from "../context/WishListProvider"; 

import { FaShoppingCart, FaRegHeart, FaSignOutAlt, FaBars, FaTimes, FaUserCircle } from "react-icons/fa"; // Added FaBars, FaTimes
import { PiUserCircleFill } from "react-icons/pi";

const Header = () => {
  const { cart } = useContext(CartContext);
  const { wishList } = useContext(WishListContext);
  const { user, logout } = useContext(AuthContext);
  // const { setKey } = useContext(KeyContext); // setKey is not used in the provided JSX, can be removed if not needed
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const drawerRef = useRef(null); // Ref for the drawer

  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api/"; 
  
  const fetchCategories = async () => {
    const res = await fetch(`${baseUrl}catagorylist`);
    if (!res.ok) throw new Error("Failed to fetch categories");
    const data = await res.json();
    return data.catagories || data.data || []; 
  };

  const { data: categories = [] } = useQuery({
    queryKey: ["categories-list-header"],
    queryFn: fetchCategories,
  });

  const handleLogout = () => {
    logout();
    setIsUserDropdownOpen(false); 
    setIsDrawerOpen(false); // Close drawer on logout
    navigate("/"); 
  };

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
  const closeDrawer = () => setIsDrawerOpen(false);

  // Close user dropdown and drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
      // Close drawer if clicking on the overlay (which is outside drawerRef.current)
      if (isDrawerOpen && drawerRef.current && !drawerRef.current.contains(event.target)) {
        // Check if the click was on the toggler button itself, if so, don't close
        const togglerButton = document.querySelector('.mobile-header-layout .navbar-toggler');
        if (togglerButton && togglerButton.contains(event.target)) {
            return;
        }
        // This logic is tricky because overlay click should close it.
        // The overlay itself has an onClick handler now.
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDrawerOpen]); // Add isDrawerOpen to dependencies if logic inside uses it
  
  const primaryColor = "#0f4372"; // From original styles
  const gradientColor = "linear-gradient(to right, #e5272e, #f59332)"; // From original styles


  return (
    <>
      <style>
        {`
          .main-header-wrapper { /* New wrapper for sticky positioning */
            position: sticky;
            top: 0;
            z-index: 1030;
            background-color: #ffffff;
            box-shadow: 0 2px 10px rgba(0,0,0,0.08);
          }
          .main-header-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.75rem 1rem; /* Base padding for header bar */
          }

          /* Mobile Header Layout (Toggler | Logo | Cart) */
          .mobile-header-layout {
            display: grid;
            grid-template-columns: auto 1fr auto; /* Toggler | Expanding Logo Space | Cart */
            align-items: center;
            width: 100%;
            gap: 10px;
          }
          .mobile-header-layout .navbar-brand {
            grid-column: 2 / 3;
            justify-self: center; 
            display: inline-block;
            padding: 0; /* Reset padding if any */
            margin: 0; /* Reset margin */
          }
          .mobile-header-layout .logo_img {
            height: 45px; 
            width: auto;
            object-fit: contain;
          }
          .mobile-header-layout .navbar-toggler {
            grid-column: 1 / 2;
            justify-self: start;
            border: none;
            padding: 0.25rem 0.5rem;
            font-size: 1.6rem; /* Larger hamburger icon */
            color: #333;
          }
          .mobile-header-layout .navbar-toggler:focus {
            box-shadow: none;
          }
          .mobile-header-layout .cart-icon-container {
            grid-column: 3 / 4;
            justify-self: end;
          }
          .mobile-header-layout .cart-icon-container .cart-btn-gradient {
            background-image: ${gradientColor};
            color: white !important;
            padding: 8px 12px !important; /* Adjusted for icon-only */
            border-radius: 8px; /* Rounded square as per screenshot */
            font-size: 1.2rem !important; 
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            display: flex;
            align-items: center;
            justify-content: center;
          }
           .mobile-header-layout .cart-icon-container .cart-btn-gradient .fa-shopping-cart {
             margin-right: 0; /* No "Cart" text next to icon */
           }
           .mobile-header-layout .cart-icon-container .cart-btn-gradient .badge {
             position: absolute;
             top: -3px;
             right: -3px;
             font-size: 0.6rem;
             padding: 0.15em 0.4em;
             background-color: white !important; /* Badge style from original */
             color: #e5272e !important;
             border-radius: 50%;
           }

          /* Drawer Styles */
          .nav-drawer {
            position: fixed;
            top: 0;
            left: -100%; /* Start off-screen */
            width: 280px; /* Drawer width, adjust as needed */
            max-width: 80%;
            height: 100vh;
            background-color: #ffffff; 
            box-shadow: 2px 0 15px rgba(0,0,0,0.15);
            z-index: 1045; /* Above overlay */
            transition: left 0.35s ease-in-out;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
          }
          .nav-drawer.open {
            left: 0; /* Slide in */
          }
          .nav-drawer-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 1rem;
            border-bottom: 1px solid #e9ecef;
          }
          .nav-drawer-header .navbar-brand .logo_img {
            height: 35px; /* Smaller logo in drawer */
          }
          .nav-drawer-close-btn {
            background: none;
            border: none;
            font-size: 1.5rem; /* Close icon size */
            color: #555;
            padding: 0.5rem;
          }
          .nav-drawer-body {
            flex-grow: 1;
            overflow-y: auto;
          }
          .nav-drawer .navbar-nav {
            flex-direction: column;
            width: 100%;
            padding: 0.5rem 0;
          }
          .nav-drawer .navbar-nav .nav-item {
            /* No border between items, use padding for separation */
          }
          .nav-drawer .navbar-nav .nav-link,
          .nav-drawer .navbar-nav .dropdown-toggle { /* Style for both links and dropdown toggles */
            color: #333;
            font-weight: 500;
            padding: 0.9rem 1.5rem;
            text-align: left;
            display: block;
            border-bottom: 1px solid #f0f0f0; /* Subtle separator */
          }
          .nav-drawer .navbar-nav .nav-item:last-child .nav-link {
             border-bottom: none;
          }
          .nav-drawer .navbar-nav .nav-link:hover,
          .nav-drawer .navbar-nav .dropdown-toggle:hover {
            background-color: #f8f9fa;
            color: ${primaryColor};
          }
          .nav-drawer .dropdown-menu { /* Styling for dropdown within drawer */
            position: static !important;
            float: none !important;
            width: 100% !important;
            border: none !important;
            box-shadow: none !important;
            background-color: #f8f9fa !important; /* Slightly different bg for dropdown section */
            margin-top: 0 !important;
            border-radius: 0;
            padding-left: 0; /* Reset padding if Bootstrap adds it */
          }
          .nav-drawer .dropdown-item {
             padding: 0.75rem 1.5rem 0.75rem 2.5rem; /* Indent dropdown items */
             color: #333;
             font-weight: 500;
          }
           .nav-drawer .dropdown-item:hover {
             background-color: #e9ecef;
             color: ${primaryColor};
           }
          
          .nav-drawer-footer { /* For icons/buttons at bottom of drawer */
            padding: 1rem 1.5rem;
            border-top: 1px solid #e9ecef;
            margin-top: auto; /* Push to bottom */
          }
           .nav-drawer-footer .header-icon-btn, 
           .nav-drawer-footer .login-btn-header,
           .nav-drawer-footer .user-actions-drawer button,
           .nav-drawer-footer .user-actions-drawer a {
             display: flex;
             align-items: center;
             width: 100%;
             text-align: left;
             margin-bottom: 0.75rem;
             padding: 0.75rem 0; /* Consistent padding */
             font-weight: 500;
             color: #333;
             text-decoration: none;
           }
            .nav-drawer-footer .header-icon-btn svg,
            .nav-drawer-footer .login-btn-header svg,
            .nav-drawer-footer .user-actions-drawer svg {
                margin-right: 10px;
                font-size: 1.1rem;
            }
           .nav-drawer-footer .header-icon-btn:last-child,
           .nav-drawer-footer .login-btn-header:last-child,
           .nav-drawer-footer .user-actions-drawer > *:last-child {
                margin-bottom: 0;
           }
           .nav-drawer-footer .login-btn-header { /* Make login button look like other items */
                background-color: transparent;
                color: #333 !important;
                padding: 0.75rem 0 !important;
                font-size: 1rem !important;
                border-radius: 0;
                box-shadow: none;
           }
            .nav-drawer-footer .login-btn-header:hover {
                background-color: #f8f9fa;
                color: ${primaryColor} !important;
            }
            .nav-drawer-footer .user-actions-drawer button {
                background: none;
                border: none;
                color: #333;
            }
            .nav-drawer-footer .user-actions-drawer button:hover,
            .nav-drawer-footer .user-actions-drawer a:hover {
                background-color: #f8f9fa;
                color: ${primaryColor};
            }

          /* Overlay for when drawer is open */
          .drawer-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0,0,0,0.5);
            z-index: 1040; /* Below drawer, above page content */
            display: none; 
            opacity: 0;
            transition: opacity 0.35s ease-in-out;
          }
          .drawer-overlay.show {
            display: block;
            opacity: 1;
          }

          /* Desktop Styles (from lg breakpoint upwards) */
          .desktop-header-layout {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
          }
          .desktop-header-layout .navbar-brand .logo_img {
            height: 80px; 
            width:80px;
          }
          .desktop-header-layout .navbar-nav {
            flex-direction: row; 
            margin-left: auto; 
            margin-right: auto; 
          }
          .desktop-header-layout .nav-link {
            color: #333;
            font-weight: 500;
            padding: 0.75rem 1rem;
            border-radius: 6px;
            transition: background-color 0.2s ease, color 0.2s ease;
          }
          .desktop-header-layout .nav-link:hover,
          .desktop-header-layout .nav-link.active {
            color: ${primaryColor};
            background-color: rgba(15, 67, 114, 0.07);
          }
          .desktop-header-layout .dropdown-menu { /* Standard Bootstrap dropdown */
            border-radius: 8px;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            border: 1px solid #eee;
          }
           .desktop-header-layout .dropdown-item {
             font-weight: 500; color: #333; padding: 0.5rem 1rem;
           }
           .desktop-header-layout .dropdown-item:hover {
             background-color: rgba(15, 67, 114, 0.07); color: ${primaryColor};
           }
          .desktop-nav-actions { 
            display: flex;
            align-items: center;
          }
          .desktop-nav-actions .header-icon-btn {
            background: none; border: none; color: ${primaryColor}; font-size: 1.3rem;
            padding: 0.5rem; margin-left: 10px; position: relative; display: flex;
            align-items: center; text-decoration: none;
          }
          .desktop-nav-actions .header-icon-btn .badge {
            position: absolute; top: -2px; right: -5px; font-size: 0.65rem;
            padding: 0.2em 0.45em; background-color: #e5272e; color: white; border-radius: 50%;
          }
          .desktop-nav-actions .cart-btn-gradient {
            background-image: ${gradientColor}; color: white !important; padding: 8px 15px !important;
            border-radius: 20px; font-size: 0.9rem !important; font-weight: 600;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1); margin-left: 15px;
            text-decoration: none; /* Added */
          }
           .desktop-nav-actions .cart-btn-gradient .fa-shopping-cart { margin-right: 5px; }
           .desktop-nav-actions .cart-btn-gradient .badge {
             background-color: white !important; color: #e5272e !important; top: -4px; right: -6px;
           }
          .desktop-nav-actions .user-menu-button {
            background: none; border: none; padding: 0; margin-left: 15px; cursor: pointer;
          }
          .desktop-nav-actions .user-menu-button .user-icon {
            font-size: 1.8rem; color: ${primaryColor};
          }
          .desktop-nav-actions .user-dropdown-menu { /* For desktop user dropdown */
            position: absolute; top: calc(100% + 5px); right: 0; background-color: white;
            border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.15);
            border: 1px solid #eee; z-index: 1050; min-width: 200px; padding: 0.5rem 0;
          }
          .desktop-nav-actions .user-dropdown-menu .dropdown-item {
            display: flex; align-items: center; font-weight: 500; color: #333;
            padding: 0.6rem 1rem; text-decoration: none;
          }
          .desktop-nav-actions .user-dropdown-menu .dropdown-item svg {
            margin-right: 10px; color: ${primaryColor};
          }
          .desktop-nav-actions .user-dropdown-menu .dropdown-item:hover {
            background-color: rgba(15, 67, 114, 0.07); color: ${primaryColor};
          }
          .desktop-nav-actions .login-btn-header {
            background-color: ${primaryColor}; color: white !important; padding: 8px 18px !important;
            border-radius: 20px; font-size: 0.9rem !important; font-weight: 600;
            margin-left: 15px; text-decoration: none;
          }
          .desktop-nav-actions .login-btn-header:hover { opacity: 0.9; }
          
          /* Custom navbar toggler icon from original styles if default is not used */
          /* .navbar-toggler-icon {
             background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(15, 67, 114, 0.8)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
          } */
        `}
      </style>

      <header className="main-header-wrapper">
        <div className="container main-header-container">
          {/* Mobile Header Layout: Toggler | Logo (Centered) | Cart : Shown only on <lg screens */}
          <div className="d-lg-none mobile-header-layout">
            <button
              className="navbar-toggler"
              type="button"
              onClick={toggleDrawer}
              aria-label="Toggle navigation"
            >
              <FaBars /> {/* Using React Icon for hamburger */}
            </button>
            <Link to="/" className="navbar-brand" onClick={closeDrawer}>
              <img src={logo} className="logo_img" alt="Booze247 Logo" />
            </Link>
            <div className="cart-icon-container">
              <Link to="/cart" className="header-icon-btn cart-btn-gradient" title="Cart">
                <FaShoppingCart />
                {cart.length > 0 && <span className="badge">{cart.length}</span>}
              </Link>
            </div>
          </div>

          {/* Desktop Header Layout: Shown only on >=lg screens */}
          <div className="d-none d-lg-flex desktop-header-layout">
            <Link to="/" className="navbar-brand">
              <img src={logo} className="logo_img" alt="Booze247 Logo" />
            </Link>
            <ul className="navbar-nav"> {/* Bootstrap class mx-auto was in original code for centering nav links */}
              <li className="nav-item"><Link className="nav-link" to="/">Home</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/shop">Shop</Link></li>
              <li className="nav-item dropdown">
                <Link className="nav-link dropdown-toggle" to="#" id="categoryDropdownDesktop" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  Shop by Category
                </Link>
                <ul className="dropdown-menu" aria-labelledby="categoryDropdownDesktop">
                  {categories && categories.length > 0 ? (
                    categories.map((category) => (
                      <li key={category._id}><Link className="dropdown-item" to={`/category/${category._id}`}>{category.catagoryname}</Link></li>
                    ))
                  ) : (
                    <li><span className="dropdown-item text-muted">Loading...</span></li>
                  )}
                </ul>
              </li>
              <li className="nav-item"><Link className="nav-link" to="/blog">Blog</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/contact">Get in Touch</Link></li>
            </ul>
            <div className="desktop-nav-actions">
              {user ? (
                <div className="position-relative" ref={userDropdownRef}>
                  <button className="user-menu-button" onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)} aria-expanded={isUserDropdownOpen} title="My Account">
                    <PiUserCircleFill className="user-icon" />
                  </button>
                  {isUserDropdownOpen && (
                    <div className="user-dropdown-menu">
                      <Link className="dropdown-item" to="/myaccount" onClick={() => setIsUserDropdownOpen(false)}><PiUserCircleFill /> My Account</Link>
                      <div className="dropdown-divider m-0"></div> {/* Bootstrap 5 m-0 if needed */}
                      <button className="dropdown-item" onClick={handleLogout}><FaSignOutAlt /> Logout</button>
                    </div>
                  )}
                </div>
              ) : (
                <Link 
                style={{
                  textDecoration:"none",
                  color:"black",
                  fontWeight:"bold",
                  fontSize:"18px",
                  display:"flex",
                  alignItems:"center",
                  gap:"4px"
                }}
                to="/login" className=""><FaUserCircle style={{
                  fontSize:"22px"
                }} /> Login</Link>
              )}
              <Link to="/wishlist" className="header-icon-btn" title="Wishlist">
                <FaRegHeart />
                {wishList.length > 0 && <span className="badge">{wishList.length}</span>}
              </Link>
              <Link to="/cart" className="header-icon-btn cart-btn-gradient" title="Cart">
                <FaShoppingCart /> Cart
                {cart.length > 0 && <span className="badge">{cart.length}</span>}
              </Link>
              
            </div>
          </div>
        </div>
      </header>

      {/* Drawer Navigation for Mobile (shown only on <lg screens) */}
      {isDrawerOpen && <div className="drawer-overlay d-lg-none" onClick={toggleDrawer}></div>}
      <div ref={drawerRef} className={`nav-drawer d-lg-none ${isDrawerOpen ? "open" : ""}`}>
        <div className="nav-drawer-header">
          <Link to="/" className="navbar-brand" onClick={closeDrawer}>
             <img src={logo} className="logo_img" style={{height: '40px'}} alt="Booze247 Logo" />
          </Link>
          <button className="nav-drawer-close-btn" onClick={toggleDrawer}><FaTimes /></button>
        </div>
        <div className="nav-drawer-body">
            <ul className="navbar-nav">
            <li className="nav-item"><Link className="nav-link" to="/" onClick={closeDrawer}>Home</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/shop" onClick={closeDrawer}>Shop</Link></li>
            <li className="nav-item dropdown">
                <Link className="nav-link dropdown-toggle" to="#" id="categoryDropdownDrawer" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                Shop by Category
                </Link>
                <ul className="dropdown-menu" aria-labelledby="categoryDropdownDrawer">
                {categories && categories.length > 0 ? (
                    categories.map((category) => (
                    <li key={category._id}><Link className="dropdown-item" to={`/category/${category._id}`} onClick={closeDrawer}>{category.catagoryname}</Link></li>
                    ))
                ) : (
                    <li><span className="dropdown-item text-muted">Loading...</span></li>
                )}
                </ul>
            </li>
            <li className="nav-item"><Link className="nav-link" to="/blog" onClick={closeDrawer}>Blog</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/contact" onClick={closeDrawer}>Get in Touch</Link></li>
            </ul>
        </div>
        <div className="nav-drawer-footer user-actions-drawer">
            <Link to="/wishlist" className="header-icon-btn" title="Wishlist" onClick={closeDrawer}>
                <FaRegHeart /> Wishlist 
                {wishList.length > 0 && <span className="badge bg-danger">{wishList.length}</span>}
            </Link>
             {/* The main cart icon is always visible in the mobile header bar,
                 so a link to cart page might be more appropriate here if included. */}
            <Link to="/cart" className="header-icon-btn" title="View Cart" onClick={closeDrawer}>
                <FaShoppingCart /> View Cart
            </Link>
            <hr className="my-2" style={{borderColor: '#dee2e6'}}/>
            {user ? (
                <>
                    <Link to="/myaccount" className="header-icon-btn" onClick={closeDrawer}>
                        <PiUserCircleFill /> My Account
                    </Link>
                    <button className="header-icon-btn" onClick={() => {handleLogout(); closeDrawer();}}>
                        <FaSignOutAlt /> Logout
                    </button>
                </>
            ) : (
                <Link to="/login" className="login-btn-header" onClick={closeDrawer}>
                    Login
                </Link>
            )}
        </div>
      </div>
    </>
  );
};

export default Header;